import type { AnalyticsFilterOptions, DayClose } from '#shared/types';
import { useCan } from '#client/hooks/useCan';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useUrlState } from '#client/hooks/useUrlState';
import { dateKey } from '#client/lib/dates';
import { ENDPOINT } from '#client/lib/endpoints';
import { dayCloseUrl } from './day-close.logic';

/**
 * The day and staff member the Day Close screen is showing, held in the URL,
 * and the figures for them.
 *
 * An admin sees the whole lab and can narrow it to one staff member; everyone
 * else sees only their own figures. The server decides that — the staff choice
 * simply is not offered to anyone it would be ignored for.
 */
export function useDayClose() {
  const can = useCan();
  const { params, setParam } = useUrlState();
  const today = dateKey(new Date());
  const on = params.get('on') ?? today;
  const canReviewAll = can('dayClose.viewAll');
  const staff = canReviewAll ? (params.get('staff') ?? '') : '';

  const close = useApiResource<DayClose>(dayCloseUrl(on, staff));
  // The analytics filter bar's staff list: the same names the rest of the app
  // shows. Fetched only for someone who can choose.
  const options = useApiResource<AnalyticsFilterOptions>(
    canReviewAll ? ENDPOINT.analyticsFilters : undefined,
  );

  return {
    close,
    on,
    today,
    staff,
    canReviewAll,
    people: options.data?.users ?? [],
    setDay: (day: string) => setParam('on', day),
    setStaff: (choice: string) => setParam('staff', choice),
  };
}
